import React, {
  MutableRefObject,
  RefObject,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import { type RewindCamPlayerHandle } from 'components/RewindCamPlayer';
import useCamRewindTimeout from 'components/RewindCamPlayer/useCamRewindTimeout/useCamRewindTimeout';
import { clipDate } from 'utils/rewinds/clipDate';
import { isTimeWithinRanges } from 'utils/rewinds/isTimeWithinRanges';
import {
  ClipsPlaylistBlock,
  SmartRewindPlaylistBlock,
  TrimmingClipPlaylistBlock,
} from 'utils/rewinds/playlistBlocks.types';
import useRefState, { RefState } from 'hooks/useRefState';
import { CurrentTimestamp } from 'hooks/useCurrentTimestamp';

interface RewindsCamPlayerWithScrubberControlContextProviderProps {
  currentPlaylist:
    | SmartRewindPlaylistBlock
    | ClipsPlaylistBlock
    | TrimmingClipPlaylistBlock
    | undefined;
  currentTimestamp: CurrentTimestamp;
  children: React.ReactChild;
}

interface RewindsCamPlayerWithScrubberControlContextProps {
  rewindCamPlayerHandle: RefObject<RewindCamPlayerHandle>;
  currentTimestampRef: RefState<Date | undefined>;
  setCurrentTimestamp: (date: Date) => void;
  sliderTimeValueRef: RefState<number | null>;
  setSliderTimeValue: (utcTimestamp: number) => void;
  playbackSpeed: number;
  setPlaybackSpeed: (speed: number) => void;
  rewindPlayerError: ErrorState | null;
  isScrubbing: boolean;
  setIsScrubbing: (isScrubbing: boolean) => void;
  isBuffering: boolean;
  setIsBuffering: (isBuffering: boolean) => void;
  sliderMissingVideo: boolean | undefined | null;
  setIsVideoPlayerLoaded: (isLoaded: boolean) => void;
  onRewindPlayerError: (errorCode: number | undefined) => void;
  handlePauseRewindPlayer: () => void;
  handlePlayRewindPlayer: () => void;
  onRewindPlay: (playing: boolean) => void;
  setPlayerTime: (utcTimeToPlay: number, wantsRewindPlaying: boolean, forcePlay?: boolean) => void;
  setUserWantsRewindPlaying: (userWantsRewindPlaying: boolean) => void;
  userWantsRewindPlaying: boolean;
  rewindPlaying: boolean;
  updateQueryStringTimestamp: MutableRefObject<() => void>;
}

interface ErrorState {
  message: string;
  altMessage?: string | null;
  subMessage?: string | null;
}

export const RewindsCamPlayerWithScrubberControlContext = createContext(
  {} as RewindsCamPlayerWithScrubberControlContextProps,
);

export const RewindsCamPlayerWithScrubberControlContextProvider = ({
  children,
  currentPlaylist,
  currentTimestamp,
}: RewindsCamPlayerWithScrubberControlContextProviderProps) => {
  const rewindCamPlayerHandle = useRef<RewindCamPlayerHandle>(null);
  const { pathname } = useRouter();

  const { currentTimestampRef, setCurrentTimestamp, updateQueryStringTimestamp } = currentTimestamp;
  const [sliderTimeValueRef, setSliderTimeValue] = useRefState<number | null>(null);

  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [rewindPlayerError, setRewindPlayerError] = useState<ErrorState | null>(null);
  const [rewindPlaying, setRewindPlaying] = useState<boolean>(false);
  const [isVideoPlayerLoaded, setIsVideoPlayerLoaded] = useState<boolean>(false);

  // iteractions / loading states
  const [isScrubbing, setIsScrubbing] = useState<boolean>(false);
  const [isBuffering, setIsBufferingState] = useState<boolean>(false);
  const bufferingTimeout = useRef<NodeJS.Timeout>();

  const setIsBuffering = useCallback((buffering: boolean) => {
    if (buffering) {
      clearTimeout(bufferingTimeout.current);
      bufferingTimeout.current = setTimeout(() => {
        setIsBufferingState(true);
      }, 500);
    }
    if (!buffering) {
      clearTimeout(bufferingTimeout.current);
      setIsBufferingState(false);
    }
  }, []);

  // We use this to determin if the user is scrubbing and wants to drop the scruber vs other actions
  const [userWantsRewindPlaying, setUserWantsRewindPlaying] = useState<boolean>(false);

  const { resetTimeout } = useCamRewindTimeout(rewindCamPlayerHandle);

  useEffect(() => {
    setRewindPlayerError(null);
  }, [currentPlaylist?.bucketStart]);

  useEffect(() => {
    resetTimeout();
  }, [isScrubbing, resetTimeout, currentPlaylist?.rewinds]);

  useEffect(() => {
    if (isScrubbing) {
      rewindCamPlayerHandle.current?.pause();
    }
    // Disabled as adding rewindPlaying as a dep breaks this
  }, [isScrubbing]);

  const handlePauseRewindPlayer = useCallback(() => {
    setUserWantsRewindPlaying(false);
    resetTimeout();
    rewindCamPlayerHandle.current?.pause();
  }, [rewindCamPlayerHandle, resetTimeout]);

  const handlePlayRewindPlayer = useCallback(() => {
    setUserWantsRewindPlaying(true);
    rewindCamPlayerHandle.current?.playAtTimestamp(currentTimestampRef.current.value as Date, true);
    resetTimeout();
  }, [rewindCamPlayerHandle, resetTimeout, currentTimestampRef]);

  const onRewindPlay = useCallback((playing: boolean) => {
    setRewindPlaying(playing);
  }, []);

  const isSliderInRewindTimeRange = useCallback(
    () =>
      currentPlaylist?.bucketStart &&
      sliderTimeValueRef.current.value &&
      sliderTimeValueRef.current.value >= currentPlaylist.bucketStart.getTime() &&
      sliderTimeValueRef.current.value <= currentPlaylist.bucketEnd.getTime(),
    [currentPlaylist?.bucketStart, currentPlaylist?.bucketEnd, sliderTimeValueRef],
  );

  const isSliderMissingVideo = useCallback(
    () =>
      Boolean(
        currentPlaylist?.rewinds &&
          sliderTimeValueRef.current.value &&
          isSliderInRewindTimeRange() &&
          !isTimeWithinRanges({
            time: new Date(sliderTimeValueRef.current.value),
            ranges:
              currentPlaylist.rewinds.map((rewind) => ({
                start: new Date(rewind.startDate),
                end: new Date(rewind.endDate),
              })) || [],
          }),
      ),
    [sliderTimeValueRef, currentPlaylist?.rewinds, isSliderInRewindTimeRange],
  );

  const [sliderMissingVideo, setSliderMissingVideo] = useState<boolean>(isSliderMissingVideo());

  const handleMissingVideo = useCallback(() => {
    const sliderCurrentlyMissingVideo = isSliderMissingVideo();
    if (sliderCurrentlyMissingVideo !== sliderMissingVideo)
      setSliderMissingVideo(sliderCurrentlyMissingVideo);
  }, [isSliderMissingVideo, sliderMissingVideo]);

  sliderTimeValueRef.current.onUpdate('handleMissingVideo', handleMissingVideo);

  useEffect(() => {
    if (isVideoPlayerLoaded && currentPlaylist?.bucketStart) {
      if (
        currentTimestampRef.current.value &&
        currentTimestampRef.current.value >= currentPlaylist.bucketStart &&
        currentTimestampRef.current.value <= currentPlaylist.bucketEnd
      ) {
        // This will not actually play the player but it will preload the timestamp to play from This is so we get a frame / still
        rewindCamPlayerHandle.current?.playAtTimestamp(currentTimestampRef.current.value);
      } else {
        rewindCamPlayerHandle.current?.playAtTimestamp(currentPlaylist.bucketStart);
      }
    }
  }, [
    currentTimestampRef,
    currentPlaylist?.bucketStart,
    currentPlaylist?.bucketEnd,
    isVideoPlayerLoaded,
    pathname,
  ]);

  const setPlayerTime = useCallback(
    (utcTimeToPlay: number, wantsRewindPlaying: boolean, forcePlay: boolean = false) => {
      if (currentPlaylist?.bucketStart) {
        const requestedTimeToPlayDate = new Date(utcTimeToPlay);
        const clippedRequestedTime = clipDate({
          date: requestedTimeToPlayDate,
          start: currentPlaylist.bucketStart,
          end: currentPlaylist.bucketEnd,
        });

        setCurrentTimestamp(clippedRequestedTime);

        const shouldPlay = wantsRewindPlaying || forcePlay;
        if (!isSliderMissingVideo() && shouldPlay) {
          setIsBuffering(true);
          setRewindPlayerError(null);
        }

        rewindCamPlayerHandle.current?.playAtTimestamp(clippedRequestedTime, shouldPlay);
        resetTimeout();
      }
    },
    [
      setCurrentTimestamp,
      currentPlaylist?.bucketStart,
      currentPlaylist?.bucketEnd,
      rewindCamPlayerHandle,
      isSliderMissingVideo,
      resetTimeout,
      setIsBuffering,
    ],
  );

  const onRewindPlayerError = useCallback((errorCode: number | undefined) => {
    setIsBuffering(false);
    if (errorCode === 2) {
      setRewindPlayerError({
        message: `Unfortunately, we are having an issue loading this Cam Rewind.
            Please try downloading the clip to view this Rewind.`,
        subMessage: errorCode ? `Error code: ${errorCode}` : null,
        altMessage: 'Cam Rewind Unavailable',
      });
    } else {
      setRewindPlayerError({
        message: `Unfortunately, we are having an issue loading this Cam Rewind.`,
        subMessage: errorCode ? `Error code: ${errorCode}` : null,
        altMessage: 'Cam Rewind Unavailable',
      });
    }
  }, []);

  useEffect(() => {
    if (sliderMissingVideo) {
      setRewindPlayerError({
        message: `Sorry, we don't have video for this time.`,
        subMessage: 'Please select another time',
      });
    } else {
      setRewindPlayerError(null);
    }
  }, [sliderMissingVideo]);

  const contextValue = useMemo(
    () => ({
      rewindCamPlayerHandle,
      currentTimestampRef,
      setCurrentTimestamp,
      sliderTimeValueRef,
      setSliderTimeValue,
      playbackSpeed,
      setPlaybackSpeed,
      isBuffering,
      setIsBuffering,
      rewindPlayerError,
      isScrubbing,
      setIsScrubbing,
      onRewindPlayerError,
      sliderMissingVideo,
      setUserWantsRewindPlaying,
      userWantsRewindPlaying,
      setIsVideoPlayerLoaded,
      handlePauseRewindPlayer,
      handlePlayRewindPlayer,
      onRewindPlay,
      setPlayerTime,
      rewindPlaying,
      updateQueryStringTimestamp,
    }),
    [
      rewindCamPlayerHandle,
      currentTimestampRef,
      setCurrentTimestamp,
      sliderTimeValueRef,
      setSliderTimeValue,
      playbackSpeed,
      setPlaybackSpeed,
      isBuffering,
      setIsBuffering,
      rewindPlayerError,
      isScrubbing,
      setIsScrubbing,
      onRewindPlayerError,
      sliderMissingVideo,
      setIsVideoPlayerLoaded,
      setUserWantsRewindPlaying,
      userWantsRewindPlaying,
      handlePauseRewindPlayer,
      handlePlayRewindPlayer,
      onRewindPlay,
      setPlayerTime,
      rewindPlaying,
      updateQueryStringTimestamp,
    ],
  );

  return (
    <RewindsCamPlayerWithScrubberControlContext.Provider value={contextValue}>
      {children}
    </RewindsCamPlayerWithScrubberControlContext.Provider>
  );
};
